import { useEffect, useMemo, useState } from 'react';
import { BarChart3, Play, Search, X, AlertTriangle, ChevronDown } from 'lucide-react';
import { Button, DataTable, EmptyState, FilterResetButton, Input, Spinner, cn } from '../../ui';
import {
  ReportCell, ReportVisual, SummaryBand, cellText, NUMERIC_TYPES, aggregateRows, isDerivedAgg, money2,
  type ReportColumn, type ReportDefinition, type ReportSummaryTile, type Tone,
} from './reportKit';
import dayjs from 'dayjs';

/**
 * Wide enough for the value, not for the header.
 *
 * A timestamp is "09 Sep 2026, 15:15" and a document number is "GB/2026/09/00186"; at narrower
 * widths both overflowed their cell — the date wrapped onto a second line (doubling the height of
 * every row in every register) and the code ran straight into the column beside it.
 */
const COL_WIDTH: Record<string, number> = {
  text: 210, list: 180, datetime: 176, code: 152, status: 112, money: 118,
  // 124, not 108: "10 Sep 2026" wrapped onto a second line at the old width, which turned every row
  // in every dated register into a 61px row.
  date: 124, decimal: 96, int: 84, percent: 84, days: 84, hours: 84,
};

/** Per report, per browser: whether the chart is unfolded. The rows are the report; the chart is an aside. */
const chartKey = (code: string) => `gb:reports:chart:${code}`;

/**
 * Everything below the parameters — and the part of the page that is actually the point.
 *
 * It is a flex column that takes all the height the workspace has left, so the table body is the
 * tallest thing on the screen and scrolls under its own sticky header. The old page let the table
 * grow to whatever height its rows needed and pushed the whole document down, which is why the
 * header scrolled away and a report that fit the viewport was rare.
 */
export function ReportResultArea({
  report, code, tone, data, isPending, isError, error, onRun, running, periodLabel,
  kpiSort, onSort, filters, onDrill, activeFilterCount, onClearFilters, className,
}: {
  report: ReportDefinition;
  code: string;
  /** The report category's hue, for the row-count tile. */
  tone: Tone;
  data: any;
  isPending: boolean;
  isError: boolean;
  error?: any;
  onRun: () => void;
  running: boolean;
  periodLabel: string;
  kpiSort: string | null;
  onSort: (key: string | null) => void;
  filters: Record<string, any>;
  onDrill: (key: string, value: string | string[] | undefined) => void;
  activeFilterCount: number;
  onClearFilters: () => void;
  className?: string;
}) {
  const [rowQuery, setRowQuery] = useState('');
  const [chartOpen, setChartOpen] = useState(false);

  useEffect(() => { setRowQuery(''); }, [code]);
  useEffect(() => {
    try { setChartOpen(localStorage.getItem(chartKey(code)) === '1'); } catch { setChartOpen(false); }
  }, [code]);
  const toggleChart = () => setChartOpen((o) => {
    try { localStorage.setItem(chartKey(code), o ? '0' : '1'); } catch { /* a preference, not state */ }
    return !o;
  });

  const columns = useMemo(() => (data?.columns ?? report.columns ?? []) as ReportColumn[], [data, report]);
  const allRows = useMemo<any[]>(() => data?.rows ?? [], [data]);
  const summary: ReportSummaryTile[] = data?.summary ?? [];
  const truncated = data?.truncated === true;
  const rowLimit = Number(data?.rowLimit) || allRows.length;
  /** How many rows the server's totals cover — more than are on screen when the screen copy is capped. */
  const totalRows = Number(data?.totalRows) || allRows.length;
  const serverTotals = useMemo<Record<string, number>>(() => data?.totals ?? {}, [data]);

  /**
   * Report rows carry no id of their own — they are whatever the SQL grouped — so each is stamped
   * with its position to key the table by.
   */
  const rows = useMemo(() => {
    const q = rowQuery.trim().toLowerCase();
    const seeded = allRows.map((r, i) => ({ ...r, __rk: i }));
    const found = q
      ? seeded.filter((r) => columns.some((c) => cellText(r[c.key], c.type).toLowerCase().includes(q)))
      : seeded;
    if (!kpiSort) return found;
    // Ordering only — the rows, the values and the totals are untouched.
    const col = columns.find((c) => c.key === kpiSort);
    const numeric = NUMERIC_TYPES.has(col?.type ?? 'text');
    return [...found].sort((a, b) => (numeric
      ? (Number(b[kpiSort]) || 0) - (Number(a[kpiSort]) || 0)
      : String(b[kpiSort] ?? '').localeCompare(String(a[kpiSort] ?? ''))));
  }, [allRows, rowQuery, columns, kpiSort]);

  /*
   * How wide each column's widest VALUE prints — a figure never wraps ("₹1,79,862.0 / 0" on two lines
   * is a broken number), so a money column is sized to its largest amount, the TOTAL line included.
   * ≈8px a character at this type size plus the cell's padding; the first 500 rows are enough.
   */
  const valueWidth = useMemo(() => {
    const w: Record<string, number> = {};
    const shown = (c: ReportColumn, v: any): string => (v === null || v === undefined || v === ''
      ? '' : c.type === 'money' ? `${money2(Math.abs(Number(v)))}${c.tone === 'balance' ? ' Dr' : Number(v) < 0 ? '-' : ''}` : cellText(v, c.type));
    for (const c of columns) {
      let max = 0;
      for (const r of allRows.slice(0, 500)) max = Math.max(max, shown(c, r[c.key]).length);
      max = Math.max(max, shown(c, serverTotals[c.key]).length);
      w[c.key] = max * 8 + 34;
    }
    return w;
  }, [columns, allRows, serverTotals]);

  const tableColumns = useMemo(() => columns.map((c) => ({
    key: c.key,
    title: c.header,
    dataIndex: c.key,
    // Wide enough for its HEADER too: an 84px number column headed "Invoices / orders" spilled its
    // title over the next one. Uppercase overline + sort arrow ≈ 7.5px a character, split over two
    // lines at most; capped so one long title cannot take the page.
    width: Math.max(
      COL_WIDTH[c.type ?? 'text'] ?? 150,
      Math.min(240, Math.ceil((c.header.length * 7.5) / 2) + 48),
      // Figures, dates and codes get their full width; free text is capped and may wrap.
      NUMERIC_TYPES.has(c.type ?? 'text') || ['date', 'datetime', 'code', 'status'].includes(c.type ?? 'text')
        ? valueWidth[c.key] ?? 0
        : Math.min(320, valueWidth[c.key] ?? 0),
    ),
    align: (NUMERIC_TYPES.has(c.type ?? 'text') ? 'right' : 'left') as 'left' | 'right',
    // A report carries every column that answers a follow-up question, but opens on the ones that
    // answer its headline question — the rest are one click away in the column picker.
    defaultVisible: !c.secondary,
    sortable: true,
    sorter: (a: any, b: any) => {
      const av = a[c.key]; const bv = b[c.key];
      if (NUMERIC_TYPES.has(c.type ?? 'text')) return (Number(av) || 0) - (Number(bv) || 0);
      if (c.type === 'date' || c.type === 'datetime') return dayjs(av).valueOf() - dayjs(bv).valueOf();
      return String(av ?? '').localeCompare(String(bv ?? ''));
    },
    render: (v: any) => <ReportCell value={v} type={c.type} tone={c.tone} lapses={c.lapses} />,
  })), [columns, valueWidth]);

  /*
   * THE TOTALS LINE — what every financial report ends with.
   *
   * By default it is the server's figure over the WHOLE result (even when the screen shows only the
   * first rows), computed by the same per-column rule as the tiles and the export's TOTAL line. When
   * the reader narrows the rows with the search box, it is recomputed over exactly the rows shown, by
   * the same rule, and says so — a total that does not match the rows under it is worse than none.
   */
  const searching = rowQuery.trim() !== '';
  const footerRow = useMemo(() => {
    const cells: Record<string, any> = {};
    let any = false;
    for (const c of columns) {
      const v = searching ? aggregateRows(c, rows) : serverTotals[c.key];
      if (v === null || v === undefined) continue;
      any = true;
      cells[c.key] = <ReportCell value={v} type={c.type} tone={c.tone} showZero derived={isDerivedAgg(c)} />;
    }
    if (!any) return undefined;
    const label = searching
      ? `Total (${rows.length.toLocaleString('en-IN')} matching)`
      : truncated ? `Total (all ${totalRows.toLocaleString('en-IN')} rows)` : 'Total';
    return { label, cells };
  }, [columns, rows, searching, serverTotals, truncated, totalRows]);

  /* ── before the first answer — the report runs by itself, so this is loading, a failure, or a period
        that cannot run ── */
  if (!data) {
    return (
      <div className={cn('flex min-h-0 flex-1 flex-col items-center justify-center rounded-card border border-dashed border-line bg-surface-2/40 p-6 text-center', className)}>
        {isPending || running ? (
          <span className="flex items-center gap-2 text-sm text-muted"><Spinner /> Running {report.name}…</span>
        ) : isError ? (
          <>
            <AlertTriangle className="size-5 text-danger-600" />
            <p className="mt-2 text-sm font-medium text-content">This report could not be run.</p>
            <p className="mt-0.5 max-w-md type-caption text-muted">
              {error?.response?.data?.error?.message ?? 'The server refused the request. Check the period and filters, then try again.'}
            </p>
            <Button className="mt-3" size="sm" variant="primary" icon={<Play size={14} />} onClick={onRun}>Try again</Button>
          </>
        ) : (
          <>
            <BarChart3 className="size-5 text-subtle" />
            <p className="mt-2 text-sm font-medium text-content">Fix the period to see the rows</p>
            <p className="mt-0.5 max-w-md type-caption text-muted">
              “From” has to be on or before “To”. The report loads as soon as the period above is valid.
            </p>
          </>
        )}
      </div>
    );
  }

  /* ── a run has answered ── */
  return (
    <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
      {!!summary.length && allRows.length > 0 && (
        <SummaryBand
          layout="strip"
          tiles={summary}
          tone={tone}
          truncated={truncated}
          sortKey={kpiSort}
          onSort={onSort}
          filters={filters}
          onDrill={onDrill}
        />
      )}

      {truncated && (
        <p className="flex items-start gap-2 border-b border-warning-500/30 bg-warning-500/[0.07] px-3 py-1.5 type-caption text-content">
          <AlertTriangle className="mt-px size-3.5 shrink-0 text-warning-600" />
          <span>
            Showing the first <strong className="font-semibold">{rowLimit.toLocaleString('en-IN')}</strong> of{' '}
            <strong className="font-semibold">{totalRows.toLocaleString('en-IN')}</strong> rows. The figures above and the
            totals line cover all {totalRows.toLocaleString('en-IN')}; export for the full list, or narrow the period or filters.
          </span>
        </p>
      )}

      {report.chart && allRows.length > 1 && chartOpen && (
        <div className="border-b border-line px-1 py-2">
          <ReportVisual chart={report.chart} rows={allRows} columns={columns} height={200} timeAxis={report.timeAxis} />
        </div>
      )}

      {allRows.length ? (
        <DataTable
          fillHeight
          className="min-h-0 flex-1 pt-2"
          storageKey={`reports:${code}`}
          columns={tableColumns}
          dataSource={rows}
          rowKey="__rk"
          footerRow={footerRow}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          emptyText={`No row matches “${rowQuery}”.`}
          toolbarLeft={
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <Input
                allowClear
                size="small"
                value={rowQuery}
                onChange={(e) => setRowQuery(e.target.value)}
                placeholder="Search these rows…"
                aria-label="Search these rows"
                prefix={<Search size={14} className="text-subtle" />}
                className="w-48"
              />
              <span className="type-caption text-subtle">
                {/* A capped answer counts against the WHOLE population: "200 rows" beside a banner saying
                    1,028 reads as two different answers to one question. */}
                {rowQuery
                  ? `${rows.length.toLocaleString('en-IN')} of ${allRows.length.toLocaleString('en-IN')}`
                  : truncated
                    ? `${allRows.length.toLocaleString('en-IN')} of ${totalRows.toLocaleString('en-IN')} rows`
                    : `${allRows.length.toLocaleString('en-IN')} row${allRows.length === 1 ? '' : 's'}`}
                {' · '}{periodLabel}
              </span>
              {/* The rows on screen are the LAST answer while a changed filter re-runs — say so, rather
                  than letting old rows sit under the new period's label with nothing moving. */}
              {running && (
                <span className="inline-flex items-center gap-1.5 type-caption text-muted" role="status">
                  <Spinner className="size-3.5" /> Updating…
                </span>
              )}
              {kpiSort && (
                <button
                  type="button"
                  onClick={() => onSort(null)}
                  className="inline-flex items-center gap-1 rounded-pill border border-brand-500/40 bg-brand-500/[0.10] px-2 py-0.5 type-label text-brand-700 transition-colors hover:brightness-95 dark:text-brand-300"
                >
                  Sorted by {columns.find((c) => c.key === kpiSort)?.header ?? kpiSort}
                  <X className="size-3" />
                </button>
              )}
            </div>
          }
          toolbarRight={
            report.chart && allRows.length > 1 ? (
              <Button
                size="sm"
                variant={chartOpen ? 'primary' : 'default'}
                icon={<BarChart3 size={14} />}
                onClick={toggleChart}
                aria-expanded={chartOpen}
              >
                Chart
                <ChevronDown className={cn('ml-0.5 size-3.5 transition-transform', chartOpen && 'rotate-180')} />
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <EmptyState
            compact
            icon={<BarChart3 />}
            title="No rows for this selection"
            description={activeFilterCount
              ? `Nothing matched. ${activeFilterCount} filter${activeFilterCount === 1 ? ' is' : 's are'} narrowing this report.`
              : report.pointInTime
                ? 'Nothing qualifies right now. This report is a snapshot of today — there is no period to widen.'
                : 'Try a wider date range.'}
            action={activeFilterCount
              ? <FilterResetButton onClick={onClearFilters} />
              : undefined}
          />
        </div>
      )}
    </div>
  );
}
